import React, { useEffect, useRef, useState } from 'react';
import { select } from 'd3-selection';
import { scaleLinear } from 'd3-scale';
import { max } from 'd3-array';
import { area, line, curveCardinal } from 'd3-shape';
import TravelGraphsHeading from '../TravelGraphsHeading';
import Styles from './AreaGraph.module.scss';

interface AreaGraphProps {
  activeIndex: number;
  ariaLabel: string;
  data: {
    data: {
      x: number;
      y: number;
    }[];
    serieId?: 'rainfall' | 'waterTemp';
  };
  dataType: string;
  enableArea: boolean;
  mobile?: boolean;
}

const AreaGraph: React.FC<AreaGraphProps> = ({
  activeIndex,
  ariaLabel,
  data,
  dataType,
  enableArea,
  mobile,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 300, height: 100 });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width: width || 300, height: height || 100 });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    if (!svgRef.current || mobile) return;

    const svg = select(svgRef.current);
    svg.selectAll('*').remove(); // Clear previous content

    const margin = { top: 20, right: 0, bottom: 20, left: 0 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    // Create scales
    const xScale = scaleLinear()
      .domain([0, data.data.length - 1])
      .range([0, width]);

    const yScale = scaleLinear()
      .domain([0, max(data.data, (d) => d.y) || 0])
      .range([height, 0]);

    // Create area generator
    const areaGenerator = area<{ x: number; y: number }>()
      .x((d) => xScale(d.x))
      .y0(height)
      .y1((d) => yScale(d.y))
      .curve(curveCardinal);

    // Create line generator
    const lineGenerator = line<{ x: number; y: number }>()
      .x((d) => xScale(d.x))
      .y((d) => yScale(d.y))
      .curve(curveCardinal);

    // Create gradient
    const defs = svg.append('defs');
    const gradient = defs
      .append('linearGradient')
      .attr('id', 'areaGradient')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');

    gradient
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#314EE6')
      .attr('stop-opacity', 0.4);

    gradient
      .append('stop')
      .attr('offset', '40%')
      .attr('stop-color', '#314EE6')
      .attr('stop-opacity', 0.2);

    gradient
      .append('stop')
      .attr('offset', '80%')
      .attr('stop-color', '#314EE6')
      .attr('stop-opacity', 0);

    // Create chart group
    const chart = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Add area if enabled
    if (enableArea) {
      chart
        .append('path')
        .datum(data.data)
        .attr('fill', 'url(#areaGradient)')
        .attr('d', areaGenerator);
    }

    // Add line
    chart
      .append('path')
      .datum(data.data)
      .attr('fill', 'none')
      .attr('stroke', '#314EE6')
      .attr('stroke-width', 0)
      .attr('d', lineGenerator);

    // Add labels - limit to 12 months for yearly data
    const monthlyData = data.data.slice(0, 12);
    const points = monthlyData.map((d) => ({
      x: xScale(d.x),
      y: yScale(d.y),
      serieId: data.serieId || 'waterTemp',
      data: { y: d.y },
    }));

    // Create label elements
    points.forEach((point, index) => {
      const labelGroup = chart.append('g');

      // Calculate proper segment positioning for 12 months
      const segmentWidth = width / 12;
      const segmentX = index * segmentWidth;
      const segmentCenterX = segmentX + segmentWidth / 2;

      // Add invisible rect for interaction
      labelGroup
        .append('rect')
        .attr('x', segmentX)
        .attr('y', 0)
        .attr('width', segmentWidth)
        .attr('height', height)
        .attr('fill', 'transparent');

      // Add text label centered in segment
      const unit = dataType === 'rainfall' ? '"' : '°';
      labelGroup
        .append('text')
        .attr('x', segmentCenterX)
        .attr('y', 35)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('fill', '#424242')
        .text(`${point.data.y}${unit}`);
    });
  }, [data, enableArea, mobile, dataType, dimensions]);

  return (
    <div className={Styles.areaGraph}>
      <TravelGraphsHeading heading={dataType === 'waterTemp' ? 'Water Temp' : dataType} />
      {mobile ? (
        <h4>
          {activeIndex >= 0 && activeIndex < data.data.length ? data.data[activeIndex].y : 0}
          {dataType === 'rainfall' ? '"' : '°'}
        </h4>
      ) : (
        <div ref={containerRef} style={{ width: '100%', height: '100px' }}>
          <svg
            ref={svgRef}
            width={dimensions.width}
            height={dimensions.height}
            style={{ overflow: 'visible' }}
            role="img"
            aria-label={`${ariaLabel} graph`}
          />
        </div>
      )}
    </div>
  );
};

export default AreaGraph;
